/**
 * CDV — Build Your Own Bundle (Simple Bundles "byob" app embed) enhancements.
 *
 * The app renders its panel client-side and covers most of the approved design
 * already. Two pieces of the design have no equivalent in the app's markup and
 * cannot be produced with CSS alone:
 *
 *   1. SIZE switcher — each bundle size is a separate product, while the app's
 *      own pack row only switches between variants of one product. The switcher
 *      markup comes from snippets/byob-size-switcher.liquid as a <template> and
 *      is injected here, replacing the app's row.
 *   2. Per-slot price — the design shows the item surcharge under every chosen
 *      product. When the bundle is configured to show per-item prices the app
 *      prints one itself; when it is not, the price is copied over from the
 *      matching product card in the picker grid.
 *   3. Progress — how many of the slots are filled, and which tile the next
 *      pick lands in. The app states this only in the CTA's "Add 3 more".
 *   4. Picker card state — how many of a flavour are already in the bundle.
 *      The app's cards say nothing about it, and on mobile the slot list is
 *      collapsed into a drawer, so a half-built bundle is invisible for as
 *      long as the shopper is choosing.
 *
 * It also publishes the height of the mobile drawer as --cdv-byob-drawer-height
 * so the stylesheet can keep the picker clear of it, and folds the sticky title
 * away once the page has been scrolled past it.
 *
 * Everything is idempotent and re-applied whenever the app re-renders.
 */
(function () {
  var SWITCHER_TEMPLATE_ID = 'cdv-byob-size-switcher';
  var PRICE_CLASS = 'cdv-resource__price';
  var SCROLL_FOLD_AT = 48;
  var MOBILE = '(max-width: 768px)';

  // `fallback` is where the switcher goes when the app prints no pack row of
  // its own — a bundle with a single pack size has nothing to switch between,
  // so the row is absent and appending would drop the switcher at the very
  // bottom of the panel.
  var SWITCHER_TARGETS = [
    {
      host: '.byob-mini-cart',
      nativeRow: '.byob-mini-cart__pack-row',
      fallback: '.byob-mini-cart__list',
    },
    {
      host: '.byob-mobile-header',
      nativeRow: '.byob-mobile-header__pack-row',
      fallback: '.byob-mobile-header__desc',
    },
  ];

  /* ------------------------------------------------------------------------
     1. SIZE switcher
     ------------------------------------------------------------------------ */
  function injectSizeSwitcher() {
    var template = document.getElementById(SWITCHER_TEMPLATE_ID);
    if (!template || !template.content.firstElementChild) return;

    SWITCHER_TARGETS.forEach(function (target) {
      var host = document.querySelector(target.host);
      if (!host) return;

      var switcher = host.querySelector('.cdv-size-switcher');
      if (!switcher) {
        switcher = template.content.firstElementChild.cloneNode(true);
        host.appendChild(switcher);
      }

      // The app renders its panel in stages, so this runs again on later
      // mutations until an anchor exists and the switcher sits in front of it.
      // Every step is a no-op once that is the case.
      var nativeRow = host.querySelector(target.nativeRow);
      if (nativeRow) nativeRow.classList.add('cdv-size-switcher-replaced');

      var anchor = nativeRow || host.querySelector(target.fallback);
      if (!anchor) return;

      if (switcher.nextElementSibling !== anchor) {
        anchor.parentNode.insertBefore(switcher, anchor);
      }
    });
  }

  /* ------------------------------------------------------------------------
     2. Per-slot price
     ------------------------------------------------------------------------ */

  /**
   * The one way a title becomes a key. Slot rows and picker cards are matched
   * by their text — there is no id shared between them — so a newline in the
   * app's markup on one side and not the other would silently match nothing,
   * and every feature keyed this way would disappear without an error.
   * @param {string} text
   * @returns {string}
   */
  function titleKey(text) {
    return String(text || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  /**
   * The product a slot row holds. The visible title first: the app appends the
   * variant to some aria-labels, and the title is what the picker card shows.
   * @param {Element} row
   * @returns {string}
   */
  function slotKey(row) {
    var title = row.querySelector('.byob-resource__title');
    return titleKey(title ? title.textContent : row.getAttribute('aria-label'));
  }

  /**
   * Builds a title -> price map from the product cards in the picker grid.
   * @returns {Object} price text keyed by normalised product title.
   */
  function readCardPrices() {
    var prices = {};

    document.querySelectorAll('.byob-bundle__product-card').forEach(function (card) {
      var title = card.querySelector('.byob-bundle__product-card-title-text');
      var price = card.querySelector('.byob-bundle__product-card-price');
      if (!title || !price) return;
      prices[titleKey(title.textContent)] = price.textContent.trim();
    });

    return prices;
  }

  function injectSlotPrices() {
    var rows = document.querySelectorAll('.byob-resource:not(.byob-resource--placeholder)');
    if (!rows.length) return;

    var prices = readCardPrices();

    rows.forEach(function (row) {
      var body = row.querySelector('.byob-resource__body');
      if (!body || body.querySelector('.' + PRICE_CLASS)) return;

      // The app prints .byob-resource__price when the bundle shows per-item
      // prices. Adding ours on top of it printed the amount twice.
      if (body.querySelector('.byob-resource__price')) return;

      var price = prices[slotKey(row)];
      if (!price) return;

      var node = document.createElement('div');
      node.className = PRICE_CLASS;
      node.textContent = price;
      body.appendChild(node);
    });
  }

  /* ------------------------------------------------------------------------
     3. Progress
     ------------------------------------------------------------------------
     Everything below writes only when the value it holds has changed. The
     bootstrap observer watches the whole document, so an unconditional write
     would schedule the pass that produced it and never settle.
     ------------------------------------------------------------------------ */

  /**
   * Reads the slot list and reports how full the bundle is.
   * @returns {{filled: number, total: number}|null} null until the app has
   *   rendered a slot list.
   */
  function readSlots() {
    var list = document.querySelector('.byob-mini-cart__list');
    if (!list) return null;

    var total = list.querySelectorAll('.byob-resource').length;
    if (!total) return null;

    return {
      filled: list.querySelectorAll('.byob-resource:not(.byob-resource--placeholder)').length,
      total: total,
    };
  }

  /**
   * @param {boolean} full
   */
  function publishCapacity(full) {
    var wrapper = document.getElementById('byob-app-embed-wrapper');
    if (!wrapper) return;

    var value = full ? 'true' : 'false';
    if (wrapper.getAttribute('data-cdv-bundle-full') !== value) {
      wrapper.setAttribute('data-cdv-bundle-full', value);
    }
  }

  function syncProgress() {
    var cart = document.querySelector('.byob-mini-cart');
    var slots = readSlots();

    // Between re-renders the list is briefly empty. Left alone, a flag set on
    // the last full pass would survive into a bundle that is no longer full
    // and leave every card reading "Bundle full".
    if (!cart || !slots) {
      publishCapacity(false);
      return;
    }

    // "3/6" beside the drawer heading. aria-live rather than a role, so a
    // reader hears the new count on the add that caused it and nothing else.
    var title = cart.querySelector('.byob-mini-cart__drawer-toggle .byob-mini-cart__title');
    if (title) {
      var count = title.parentNode.querySelector('.cdv-byob-count');
      if (!count) {
        count = document.createElement('span');
        count.className = 'cdv-byob-count';
        // Read out by the sibling status below, in a sentence.
      count.setAttribute('aria-hidden', 'true');
        title.insertAdjacentElement('afterend', count);
      }

      var label = slots.filled + '/' + slots.total;
      if (count.textContent !== label) count.textContent = label;

      var done = slots.filled >= slots.total;
      if (count.classList.contains('cdv-byob-count--done') !== done) {
        count.classList.toggle('cdv-byob-count--done', done);
      }

      var status = title.parentNode.querySelector('.cdv-byob-status');
      if (!status) {
        status = document.createElement('span');
        status.className = 'cdv-byob-status';
        status.setAttribute('aria-live', 'polite');
        count.insertAdjacentElement('afterend', status);
      }

      var spoken = done
        ? 'Bundle complete, ' + slots.total + ' of ' + slots.total + ' chosen'
        : slots.filled + ' of ' + slots.total + ' chosen';
      if (status.textContent !== spoken) status.textContent = spoken;
    }

    var toggle = cart.querySelector('.byob-mini-cart__drawer-toggle');
    var bar = cart.querySelector('.cdv-byob-progress');
    if (!bar && toggle) {
      bar = document.createElement('div');
      bar.className = 'cdv-byob-progress';
      // The count beside the heading already says this out loud.
      bar.setAttribute('aria-hidden', 'true');
      bar.innerHTML = '<div class="cdv-byob-progress__fill"></div>';
      toggle.insertAdjacentElement('afterend', bar);
    }

    if (bar) {
      var ratio = (slots.filled / slots.total).toFixed(4);
      if (bar.style.getPropertyValue('--cdv-progress') !== ratio) {
        bar.style.setProperty('--cdv-progress', ratio);
      }
    }

    // Published for the stylesheet: at capacity the app swaps every add button
    // for its own "Max quantity added", which is also its copy for a single
    // product hitting its own limit. Only here is the difference knowable.
    publishCapacity(slots.filled >= slots.total);

    // Each product modal says how many slots its quantity can still fill:
    // the app caps the stepper at the remaining space without saying why.
    var left = slots.total - slots.filled;
    var note = left <= 0
      ? 'Your bundle is full'
      : left === 1
        ? '1 slot left in your bundle'
        : left + ' slots left in your bundle';

    document.querySelectorAll('.byob-options-modal__selectors').forEach(function (selectors) {
      var line = selectors.querySelector('.cdv-modal-slots');
      if (!line) {
        line = document.createElement('p');
        line.className = 'cdv-modal-slots';
        selectors.appendChild(line);
      }
      if (line.textContent !== note) line.textContent = note;
    });

    // The first empty tile is where the next pick lands, so it is the one that
    // carries the plus rather than every empty tile carrying nothing.
    var next = cart.querySelector('.byob-mini-cart__list .byob-resource--placeholder');
    cart.querySelectorAll('.cdv-slot--next').forEach(function (el) {
      if (el !== next) el.classList.remove('cdv-slot--next');
    });
    if (next && !next.classList.contains('cdv-slot--next')) {
      next.classList.add('cdv-slot--next');
    }
  }

  /* ------------------------------------------------------------------------
     4. Picker card state
     ------------------------------------------------------------------------ */

  /**
   * Counts the slots each product occupies. A flavour can be taken more than
   * once, so the number matters as much as the fact of having been chosen.
   * @returns {Object} slot count keyed by normalised product title.
   */
  function readSlotCounts() {
    var counts = {};

    document
      .querySelectorAll('.byob-mini-cart__list .byob-resource:not(.byob-resource--placeholder)')
      .forEach(function (row) {
        var key = slotKey(row);
        if (key) counts[key] = (counts[key] || 0) + 1;
      });

    return counts;
  }

  function syncCardCounts() {
    var cards = document.querySelectorAll('.byob-bundle__product-card');
    if (!cards.length) return;

    var counts = readSlotCounts();

    cards.forEach(function (card) {
      var title = card.querySelector('.byob-bundle__product-card-title-text');
      var media = card.querySelector('.byob-bundle__product-card-media');
      if (!title || !media) return;

      var taken = counts[titleKey(title.textContent)] || 0;
      var badge = media.querySelector('.cdv-card-count');

      if (taken > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'cdv-card-count';
          // The slot list is the accessible account of what is in the bundle;
          // this is the same fact drawn on the card.
          badge.setAttribute('aria-hidden', 'true');
          media.appendChild(badge);
        }
        if (badge.textContent !== String(taken)) badge.textContent = String(taken);
      } else if (badge) {
        badge.remove();
      }

      // The grid that puts a control on the image is on the custom element
      // (section 10b), so anywhere else the button lands under the title.
      // Early passes can run before the app has upgraded it.
      var host = card.querySelector('byob-product-picker-card');
      // Scoped to the card, not to the host: a button left behind by a pass
      // that ran before the upgrade is outside the host and would otherwise be
      // neither found nor cleaned up, leaving two in one cell.
      var buttons = card.querySelectorAll('.cdv-cta-remove');
      var remove = buttons[0] || null;

      for (var extra = 1; extra < buttons.length; extra++) {
        buttons[extra].remove();
      }

      if (taken > 0 && host) {
        if (!remove || remove.parentNode !== host) {
          if (remove) remove.remove();
          remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'cdv-cta-remove';
          remove.textContent = '\u2212';
          // First, so the tab order matches the reading order: the button is
          // on the left of the image and the app's add button on the right.
          host.insertBefore(remove, host.firstChild);
        }
        var label = 'Remove one ' + title.textContent.trim() + ' from the bundle';
        if (remove.getAttribute('aria-label') !== label) {
          remove.setAttribute('aria-label', label);
        }
      } else if (remove) {
        // Removing the last one takes the focused element away with it, which
        // drops focus to the body and restarts tabbing at the top of the grid.
        if (remove.contains(document.activeElement) || remove === document.activeElement) {
          var next = card.querySelector('.byob-cta--add') || card.querySelector('a, button');
          if (next && next.focus) next.focus();
        }
        remove.remove();
      }

      if (card.classList.contains('cdv-card--in-bundle') !== taken > 0) {
        card.classList.toggle('cdv-card--in-bundle', taken > 0);
      }
    });
  }

  /* ------------------------------------------------------------------------
     5. Mobile drawer height
     ------------------------------------------------------------------------
     Below 768px the panel is a drawer fixed to the bottom of the viewport, so
     it reserves no space in the flow and would cover the last rows of the
     picker. Its height depends on the slot count and on whether it is
     expanded, which the stylesheet cannot know, so it is measured here and
     read back from --cdv-byob-drawer-height.

     ------------------------------------------------------------------------ */
  var drawerObserver = null;
  var observedDrawer = null;

  function publishDrawerHeight() {
    var wrapper = document.getElementById('byob-app-embed-wrapper');
    if (!wrapper || !observedDrawer) return;

    // Only the collapsed drawer is measured. Expanded it covers most of the
    // viewport, and reserving that much would leave the picker trailing a
    // screen of empty space once the drawer is closed again — while it is open
    // there is nothing to keep clear of anyway. The observer fires again on
    // collapse, so the value is never stale for long.
    if (observedDrawer.getAttribute('data-drawer-expanded') === 'true') return;

    wrapper.style.setProperty(
      '--cdv-byob-drawer-height',
      Math.round(observedDrawer.getBoundingClientRect().height) + 'px'
    );
  }

  function trackDrawerHeight() {
    if (typeof ResizeObserver === 'undefined') return;

    var drawer = document.querySelector('.byob-mini-cart');
    if (!drawer || drawer === observedDrawer) return;

    if (!drawerObserver) drawerObserver = new ResizeObserver(publishDrawerHeight);
    if (observedDrawer) drawerObserver.unobserve(observedDrawer);

    observedDrawer = drawer;
    drawerObserver.observe(drawer);
  }

  /* ------------------------------------------------------------------------
     6. Interactions
     ------------------------------------------------------------------------ */

  /**
   * A tap on the add button confirms itself on the card, and a tap anywhere on
   * the drawer heading opens the drawer — the app listens only on its own
   * "View items" link, a small target for the one control that reveals what is
   * in the bundle.
   */
  /**
   * Collapses the drawer without going through its own toggle. Both callers
   * synthesise a click on the app's control, which lands back in onClick.
   * @param {Element} control the app's toggle or close button
   */
  function forwardTo(control) {
    if (!control || forwarding) return;
    forwarding = true;
    try {
      control.click();
    } finally {
      forwarding = false;
    }
  }

  function onClick(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var remove = target.closest('.cdv-cta-remove');
    if (remove) {
      // The app's card has no handler for this; its media does, and it opens
      // the options modal, so the event must not reach it.
      event.preventDefault();
      event.stopPropagation();
      removeOne(remove.closest('.byob-bundle__product-card'));
      return;
    }

    // A sheet closes when the page behind it is tapped. The dimming is a
    // spread shadow (section 10b) and a shadow is not hit-tested, so without
    // this the only way out is the chevron.
    //
    // The tap itself is let through. It used to be swallowed so that it did
    // nothing but close the sheet — and the forwarded close does not land
    // while the app is still inside the click it came from, so a tap on a
    // product card behind the open sheet did nothing at all: no product
    // modal, no add, and the sheet stayed open. Now the card gets its tap and
    // the sheet closes on the next turn, once that click has finished.
    var cart = document.querySelector('.byob-mini-cart');
    if (
      cart &&
      cart.getAttribute('data-drawer-expanded') === 'true' &&
      !cart.contains(target) &&
      // A real tap, not Enter on a focused control and not a click this file
      // synthesised: both of those report a detail of 0.
      event.detail > 0 &&
      window.matchMedia(MOBILE).matches
    ) {
      window.setTimeout(function () {
        if (cart.getAttribute('data-drawer-expanded') !== 'true') return;
        forwardTo(cart.querySelector('.byob-mini-cart__close-btn, .byob-mini-cart__toggle-btn'));
      }, 0);
    }

    var add = target.closest('.byob-cta--add');
    if (add) {
      var card = add.closest('.byob-bundle__product-card');
      if (!card) return;
      card.classList.remove('cdv-card--pop');
      // Restarts the animation when the same card is tapped twice.
      void card.offsetWidth;
      card.classList.add('cdv-card--pop');
      window.setTimeout(function () {
        card.classList.remove('cdv-card--pop');
      }, 460);
      return;
    }

    // Only below the app's own breakpoint: above it the panel is a column on
    // the page, not a drawer, and its heading is not a control.
    if (!window.matchMedia(MOBILE).matches) return;

    var heading = target.closest('.byob-mini-cart__drawer-toggle');
    // A control inside the heading is already wired to something of its own.
    if (!heading || target.closest('button, a')) return;

    forwardTo(heading.querySelector('.byob-mini-cart__toggle-btn, .byob-mini-cart__close-btn'));
  }

  /**
   * Drops the most recently taken slot for a product, by clicking the row's
   * own remove button — the app owns the bundle state and this is the only
   * way in. Newest rather than first so that repeated taps undo in the order
   * they were made.
   * @param {Element|null} card the picker card the control belongs to
   */
  function removeOne(card) {
    var title = card && card.querySelector('.byob-bundle__product-card-title-text');
    if (!title) return;

    var key = title.textContent.trim().toLowerCase();
    var newest = null;

    document
      .querySelectorAll('.byob-mini-cart__list .byob-resource:not(.byob-resource--placeholder)')
      .forEach(function (row) {
        var titleNode = row.querySelector('.byob-resource__title');
        var rowKey = (row.getAttribute('aria-label') || (titleNode ? titleNode.textContent : ''))
          .trim()
          .toLowerCase();
        if (rowKey === key) newest = row;
      });

    var button = newest && newest.querySelector('.byob-resource__remove');
    if (button) button.click();
  }

  /** Escape closes the sheet, as it does every other overlay. */
  function onKeydown(event) {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;

    var cart = document.querySelector('.byob-mini-cart');
    if (!cart || cart.getAttribute('data-drawer-expanded') !== 'true') return;
    if (!window.matchMedia(MOBILE).matches) return;

    forwardTo(cart.querySelector('.byob-mini-cart__close-btn, .byob-mini-cart__toggle-btn'));
  }

  var forwarding = false;
  var folded = null;

  function onScroll() {
    var wrapper = document.getElementById('byob-app-embed-wrapper');
    if (!wrapper) return;

    var next = window.pageYOffset > SCROLL_FOLD_AT ? 'true' : 'false';
    if (next === folded) return;

    folded = next;
    wrapper.setAttribute('data-cdv-scrolled', next);
  }

  /* ------------------------------------------------------------------------
     7. Product modal options
     ------------------------------------------------------------------------ */

  /**
   * The app asks for a variant with a native dropdown, the only one in the
   * builder; the product page asks the same question with a row of buttons.
   * Each select gets that row, and the select stays — hidden — as the app's
   * own control: a button writes the value into it and fires the event the
   * app listens for, so variant, price and the add all stay the app's.
   *
   * The row is appended at the end of the selectors and moved up with CSS
   * `order`, like the slots line in section 3: the app re-renders this block,
   * and a node of ours between its children would be matched against one of
   * its own.
   */
  function injectModalOptions() {
    document.querySelectorAll('.byob-options-modal__selectors').forEach(function (selectors) {
      selectors.querySelectorAll('.byob-bundle__product-variant-select').forEach(function (select, index) {
        var group = selectors.querySelector('.cdv-modal-option[data-index="' + index + '"]');
        if (!group) {
          group = buildModalOption(select, index);
          var slotsLine = selectors.querySelector('.cdv-modal-slots');
          selectors.insertBefore(group, slotsLine);
        }

        var wrap = select.closest('.byob-bundle__product-variant-select-wrap');
        if (wrap && !wrap.classList.contains('cdv-variant-select-replaced')) {
          wrap.classList.add('cdv-variant-select-replaced');
        }

        group.querySelectorAll('.cdv-modal-option__value').forEach(function (button) {
          var option = select.options[Number(button.getAttribute('data-option'))];
          var pressed = option && option.value === select.value ? 'true' : 'false';
          if (button.getAttribute('aria-pressed') !== pressed) {
            button.setAttribute('aria-pressed', pressed);
          }
          button.disabled = !option || option.disabled;
        });
      });
    });
  }

  /**
   * @param {HTMLSelectElement} select the app's dropdown for one option
   * @param {number} index its position among the modal's selects
   * @returns {HTMLFieldSetElement}
   */
  function buildModalOption(select, index) {
    var group = document.createElement('fieldset');
    group.className = 'cdv-modal-option';
    group.setAttribute('data-index', String(index));

    var legend = document.createElement('legend');
    legend.className = 'cdv-modal-option__label';
    legend.textContent = (select.getAttribute('aria-label') || 'Size') + ':';
    group.appendChild(legend);

    var values = document.createElement('div');
    values.className = 'cdv-modal-option__values';
    group.appendChild(values);

    Array.prototype.forEach.call(select.options, function (option, optionIndex) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'cdv-modal-option__value';
      button.setAttribute('data-option', String(optionIndex));
      button.textContent = option.textContent.trim();
      button.addEventListener('click', function () {
        if (select.value === option.value) return;
        select.value = option.value;
        // `input` is the one the app reads; `change` for anything else.
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
        injectModalOptions();
      });
      values.appendChild(button);
    });

    return group;
  }

  /* ------------------------------------------------------------------------
     Bootstrap
     ------------------------------------------------------------------------ */
  /* ------------------------------------------------------------------------
     8. Desktop: keep the new slot in view
     ------------------------------------------------------------------------ */
  var lastFilled = null;

  /**
   * The panel shows every slot, so on a short viewport part of it is off
   * screen. After an add it moves just enough for the new slot to show, and
   * the CTA once the bundle is full.
   */
  function revealNewSlot() {
    var slots = readSlots();
    if (!slots) return;

    var grew = lastFilled !== null && slots.filled > lastFilled;
    lastFilled = slots.filled;
    if (!grew || window.matchMedia(MOBILE).matches) return;

    var wrapper = document.getElementById('byob-app-embed-wrapper');
    var cart = document.querySelector('.byob-mini-cart');
    var cta = cart && cart.querySelector('.byob-mini-cart__checkout');
    var rows = cart ? cart.querySelectorAll('.byob-resource:not(.byob-resource--placeholder)') : [];
    var row = rows[rows.length - 1];
    if (!wrapper || !cta || !row) return;

    var cartRect = cart.getBoundingClientRect();
    var viewport = window.innerHeight;
    var header = parseFloat(getComputedStyle(cart).getPropertyValue('--header-height')) || 80;
    var highest = header + 24;
    var rowTop = row.getBoundingClientRect().top - cartRect.top;
    var lowest = viewport - 24 - (cta.getBoundingClientRect().bottom - cartRect.top);

    // As low as the CTA allows, unless that hides the new row above the fold.
    var top = Math.min(highest, Math.max(lowest, highest - rowTop));
    var stuck = Math.abs(cartRect.top - parseFloat(getComputedStyle(cart).top)) < 2;
    wrapper.style.setProperty('--cdv-byob-panel-top', Math.round(top) + 'px');
    if (stuck) return;

    // Not stuck yet, the panel only moves with the page.
    var target = slots.filled === slots.total ? cta : row;
    var overflow = target.getBoundingClientRect().bottom + 24 - viewport;
    if (overflow > 0) {
      // From 990px the theme scrolls .page-wrapper, not the document.
      var scroller = window.matchMedia('(min-width: 990px)').matches
        ? document.querySelector('.page-wrapper') || document.scrollingElement
        : document.scrollingElement;
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      scroller.scrollBy({ top: overflow, behavior: reduce ? 'auto' : 'smooth' });
    }
  }

  function resetPanelTop() {
    var wrapper = document.getElementById('byob-app-embed-wrapper');
    if (wrapper) wrapper.style.removeProperty('--cdv-byob-panel-top');
  }

  var scheduled = false;

  function apply() {
    scheduled = false;
    injectSizeSwitcher();
    injectSlotPrices();
    syncProgress();
    syncCardCounts();
    injectModalOptions();
    trackDrawerHeight();
    revealNewSlot();
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(apply);
  }

  function start() {
    if (!document.getElementById('byob-app-embed-wrapper') && !document.getElementById(SWITCHER_TEMPLATE_ID)) {
      return;
    }

    apply();
    onScroll();

    // Capture phase: the app handles the add button's click itself, and a
    // listener that waited for the bubble would be racing its re-render.
    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKeydown);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', resetPanelTop);

    // Kept alive for the life of the page: the app re-renders the slot list on
    // every add and remove, which would otherwise drop everything injected
    // above. Every write is idempotent and skipped when the value has not
    // changed, so re-running on mutations settles after a single pass.
    //
    // Scoped to the embed rather than the document: everything this file reads
    // or writes is inside it, and on the body every unrelated widget on the
    // page — a chat bubble, a carousel — would schedule a pass for nothing.
    var root = document.getElementById('byob-app-embed-wrapper') || document.body;
    new MutationObserver(schedule).observe(root, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
